/**
 * 市场项目筛选、统计与地理范围工具
 */

import { resolveProvinceAdcodeLoose } from '../components/Map/provinceAdcode';
import {
  DEFAULT_PROJECTS_DATA_FILTER,
  MARKET_BUSINESS_TYPE_CATALOG,
  MARKET_DEPARTMENT_CATALOG,
  MARKET_INDUSTRY_CATALOG,
  MARKET_REGION_CATALOG,
  type MarketFilterOptions,
  type MarketProject,
  type MarketProjectStatistics,
  type ProjectCategory,
  type ProjectCategoryFilter,
  type ProjectsDataFilter,
} from '../types/market';

const ZH_LOCALE = 'zh-Hans-CN';

/** 侧栏筛选是否偏离默认（用于全国图「恢复默认」按需显示） */
export function isProjectsFilterDirty(filter: ProjectsDataFilter): boolean {
  if (filter.category !== DEFAULT_PROJECTS_DATA_FILTER.category) return true;
  if (filter.keyword?.trim()) return true;
  if (filter.industries?.length) return true;
  if (filter.regions?.length) return true;
  if (filter.businessTypes?.length) return true;
  if (filter.departments?.length) return true;
  return false;
}

export function countProjectsByCategory(
  projects: MarketProject[],
): MarketProjectStatistics {
  return {
    preliminary: projects.filter((p) => p.category === 'preliminary').length,
    performance: projects.filter((p) => p.category === 'performance').length,
  };
}

export function projectsInProvince(
  projects: MarketProject[],
  adcode: string,
): MarketProject[] {
  return projects.filter(
    (p) =>
      !p.isOverseas && resolveProvinceAdcodeLoose(p.province) === adcode,
  );
}

export function projectHasMapCoords(p: MarketProject): boolean {
  return p.latitude != null && p.longitude != null;
}

/** 国内项误用海外示意坐标 (20, 109) 等 */
function isSuspiciousDomesticPlaceholderCoord(p: MarketProject): boolean {
  if (p.isOverseas || p.latitude == null || p.longitude == null) return false;
  const lat = p.latitude;
  const lng = p.longitude;
  return (
    (Math.abs(lat - 20) < 0.05 && Math.abs(lng - 109) < 0.05) ||
    (Math.abs(lat - 20.5) < 0.05 && Math.abs(lng - 100.5) < 0.05) ||
    (Math.abs(lat - 20) < 0.05 && Math.abs(lng - 118.5) < 0.05)
  );
}

/** 省下钻时过滤落在省界外包框外的点（粗略 bbox，adcode 与 DataV 一致） */
const PROVINCE_DRILL_BBOX: Record<
  string,
  { minLng: number; maxLng: number; minLat: number; maxLat: number }
> = {
  '530000': { minLng: 97.5, maxLng: 106.3, minLat: 21.0, maxLat: 29.4 },
};

function isInsideProvinceDrillBbox(
  p: MarketProject,
  provinceAdcode: string,
): boolean {
  if (p.latitude == null || p.longitude == null) return false;
  const box = PROVINCE_DRILL_BBOX[provinceAdcode];
  if (!box) return true;
  const { minLng, maxLng, minLat, maxLat } = box;
  return (
    p.longitude >= minLng &&
    p.longitude <= maxLng &&
    p.latitude >= minLat &&
    p.latitude <= maxLat
  );
}

export function projectsForMap(
  projects: MarketProject[],
  options?: { hideOverseas?: boolean; provinceAdcode?: string | null },
): MarketProject[] {
  return projects.filter((p) => {
    if (!projectHasMapCoords(p)) return false;
    if (options?.hideOverseas && p.isOverseas) return false;
    if (isSuspiciousDomesticPlaceholderCoord(p)) return false;
    if (
      options?.provinceAdcode &&
      !p.isOverseas &&
      !isInsideProvinceDrillBbox(p, options.provinceAdcode)
    ) {
      return false;
    }
    return true;
  });
}

function matchesKeyword(p: MarketProject, keyword: string): boolean {
  const k = keyword.toLowerCase();
  const parts = [
    p.id,
    p.code,
    p.name,
    p.location,
    p.province,
    p.city,
    p.region,
    p.industry,
    p.businessType,
    p.client,
    p.department,
    p.leader,
    p.owner,
    p.overview,
  ];
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(k);
}

function matchesMulti(
  value: string,
  selected: string[] | undefined,
): boolean {
  if (!selected || selected.length === 0) return true;
  return selected.includes(value);
}

export function filterMarketProjects(
  projects: MarketProject[],
  filter: ProjectsDataFilter,
): MarketProject[] {
  return projects.filter((p) => {
    if (filter.category !== 'all' && p.category !== filter.category) return false;

    const kw = filter.keyword?.trim();
    if (kw && !matchesKeyword(p, kw)) return false;

    if (filter.category === 'preliminary') {
      if (!matchesMulti(p.industry, filter.industries)) return false;
      if (!matchesMulti(p.region, filter.regions)) return false;
    }

    if (filter.category === 'performance') {
      if (!matchesMulti(p.businessType, filter.businessTypes)) return false;
      if (!matchesMulti(p.department, filter.departments)) return false;
    }

    return true;
  });
}

function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter((v) => v.trim()))).sort((a, b) =>
    a.localeCompare(b, ZH_LOCALE),
  );
}

/** 固定字典在前，数据中多出的值按拼音排在后面 */
export function mergeCatalogOptions(
  catalog: readonly string[],
  liveValues: string[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of catalog) {
    const value = item.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  for (const item of uniqueSorted(liveValues)) {
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

export function collectFilterOptions(
  projects: MarketProject[],
): MarketFilterOptions {
  return collectFilterOptionsForCategory(projects, 'all');
}

export function collectFilterOptionsForCategory(
  projects: MarketProject[],
  category: ProjectCategoryFilter,
): MarketFilterOptions {
  const scoped =
    category === 'all'
      ? projects
      : projects.filter((p) => p.category === category);

  if (category === 'all') {
    return {
      industries: [],
      regions: [],
      businessTypes: [],
      departments: [],
    };
  }

  if (category === 'preliminary') {
    return {
      industries: mergeCatalogOptions(
        MARKET_INDUSTRY_CATALOG,
        scoped.map((p) => p.industry),
      ),
      regions: mergeCatalogOptions(
        MARKET_REGION_CATALOG,
        scoped.map((p) => p.region),
      ),
      businessTypes: [],
      departments: [],
    };
  }

  return {
    industries: [],
    regions: [],
    businessTypes: mergeCatalogOptions(
      MARKET_BUSINESS_TYPE_CATALOG,
      scoped.map((p) => p.businessType),
    ),
    departments: mergeCatalogOptions(
      MARKET_DEPARTMENT_CATALOG,
      scoped.map((p) => p.department),
    ),
  };
}

export function getProjectAmount(p: MarketProject): number {
  const amount = p.contractAmountWan;
  return amount != null && Number.isFinite(amount) ? amount : 0;
}

export function sortMarketProjects(projects: MarketProject[]): MarketProject[] {
  return [...projects].sort((a, b) => {
    if (a.isOverseas !== b.isOverseas) {
      return a.isOverseas ? 1 : -1;
    }
    const amtDiff = getProjectAmount(b) - getProjectAmount(a);
    if (amtDiff !== 0) return amtDiff;
    if (a.category !== b.category) {
      return a.category === 'preliminary' ? -1 : 1;
    }
    const p = (a.province || '').localeCompare(b.province || '', ZH_LOCALE);
    if (p !== 0) return p;
    return (a.name || '').localeCompare(b.name || '', ZH_LOCALE);
  });
}

export function projectCategoryLabel(category: ProjectCategory): string {
  return category === 'preliminary' ? '前期项目' : '履约项目';
}

export function projectListSubtitle(p: MarketProject): string {
  const region = p.isOverseas
    ? p.country || p.province || '海外'
    : p.province;
  const parts = [region, p.industry, p.department].filter(Boolean);
  if (!projectHasMapCoords(p)) {
    parts.push('地图未定位');
  }
  const amount = formatAmountBrief(getProjectAmount(p) || null);
  if (amount) parts.push(amount);
  return parts.join(' · ') || '—';
}

export function formatAmountBrief(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return '';
  return `${amount}万`;
}

/** 切换分类时清理不适用的筛选项 */
export function sanitizeFilterForCategory(
  filter: ProjectsDataFilter,
  category: ProjectsDataFilter['category'],
): ProjectsDataFilter {
  const next: ProjectsDataFilter = { ...filter, category };
  if (category === 'preliminary') {
    delete next.businessTypes;
    delete next.departments;
    delete next.industries;
    delete next.regions;
  } else if (category === 'performance') {
    delete next.industries;
    delete next.regions;
    delete next.businessTypes;
    delete next.departments;
  } else {
    delete next.industries;
    delete next.regions;
    delete next.businessTypes;
    delete next.departments;
  }
  return next;
}

export function formatAmountWanYuan(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `${amount} 万元`;
}

export function formatCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
  options?: { isOverseas?: boolean; country?: string },
): string {
  if (options?.isOverseas) {
    const country = options.country?.trim() || '海外';
    if (lat != null && lng != null) {
      return `海外项目（${country}）· 地图示意位置`;
    }
    return `海外项目（${country}）`;
  }
  if (lat == null || lng == null) return '暂无国内坐标';
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}
